import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  /**
   * Stillwell: whether a stepper change is still in its 300ms wait (not sent yet) in any cart list. The "Both knees"
   * card (snippets/sw-head.liquid) waits for it: a + tapped just before the card was dropped (400 on the old line).
   * @returns {boolean}
   */
  static get waiting() {
    return Array.from(document.querySelectorAll('cart-items-component')).some(
      (el) => el instanceof CartItemsComponent && el.#waiting.size > 0
    );
  }

  /**
   * Stillwell: section HTML already put in place by `renderSectionHtml` (keyed by section id), so the cart event
   * that carries the same HTML does not morph the cart a second time.
   * @type {Map<string, string>}
   */
  static #renderedEarly = new Map();

  /**
   * Stillwell: puts the cart section HTML in place before the cart event settles. sl-ajax-cart.js calls it with the
   * /cart/add.js response, so the drawer can open complete and at once instead of waiting for /cart.json.
   * Plain morph: no view transition and no row animation (the drawer is closed while this runs).
   * @param {string} html - Section Rendering API markup for this component's section.
   */
  async renderSectionHtml(html) {
    const sectionId = this.sectionId;
    const injectStylesheet = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
    sectionRenderer.abortRender(sectionId);
    await morphSection(sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full', injectStylesheet });
    CartItemsComponent.#renderedEarly.set(sectionId, html);
    this.#updateCartQuantitySelectorButtonStates();
    // cart-drawer-component re-measures its sticky summary on this (needed when the drawer is already open)
    const live = this.isConnected ? this : document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
    live?.dispatchEvent(new Event('cart-items:rendered', { bubbles: true }));
  }

  /**
   * Stillwell: one 300ms debounce per line (a single shared one dropped line A's change when line B was tapped within
   * 300ms).
   * @type {Map<string, (event: QuantitySelectorUpdateEvent) => void>}
   */
  #lineDebounces = new Map();

  /** @param {Event} event */
  #debouncedOnChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    // Stillwell: whether the stepper belongs to this list, and which line it is, are read now. With a discount code
    // a re-render during the 300ms wait replaces the row (Shopify gives the line a new key), and the change used to be
    // dropped as "not in this list".
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const tappedRow = this.#rowOf(event);
    const tappedKey = tappedRow?.dataset.key;
    if (tappedKey) this.#eventKeys.set(event, tappedKey);
    const tappedInput = tappedRow?.querySelector('cart-quantity-selector-component input');
    if (tappedInput instanceof HTMLInputElement) this.#eventBases.set(event, tappedInput.defaultValue);
    const id = String(event.detail?.cartLine ?? '');
    let run = this.#lineDebounces.get(id);
    if (!run) {
      run = debounce((/** @type {QuantitySelectorUpdateEvent} */ e) => {
        this.#waiting.delete(id);
        this.#onQuantityChange(e);
        CartItemsComponent.#settleSoon();
      }, 300);
      this.#lineDebounces.set(id, run);
    }
    this.#waiting.add(id);
    run(event);
  };

  /**
   * Stillwell: cart changes from the line items run one after another. A tap on + or - while a change was on its way
   * used to hit nothing (the list ignored taps until the answer was in); now the stepper keeps counting and the new
   * quantity is sent once the previous change has landed.
   * @type {Promise<void>}
   */
  static #queue = Promise.resolve();

  /**
   * Stillwell: holds the stepper queue while another cart change runs (a discount code, assets/cart-discount.js): it
   * re-keys every line, so a stepper change sent meanwhile went out under the old key and was lost (400). Changes
   * already queued go first; the returned function lets the queue go on (call it once the new cart is in place).
   * @returns {{ ahead: Promise<unknown>, release: () => void }}
   */
  static hold() {
    /** @type {() => void} */
    let release = () => {};
    const gate = new Promise((resolve) => {
      release = () => resolve(undefined);
    });
    const ahead = Promise.all(CartItemsComponent.pendingQuantityUpdates);
    CartItemsComponent.#queue = CartItemsComponent.#queue.then(() => gate);
    // Never held for long: a lost answer must not freeze the steppers
    const timer = setTimeout(release, 15000);
    return {
      ahead,
      release: () => {
        clearTimeout(timer);
        release();
      },
    };
  }
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * Stillwell: keys of lines whose removal is on its way. A re-render from an earlier change can bring such a line
   * back for a moment; it stays hidden until its own removal lands.
   * @type {Set<string>}
   */
  static #removing = new Set();

  /** @type {WeakMap<Event, string>} Stillwell: the line key of each stepper event, read when it was dispatched */
  #eventKeys = new WeakMap();

  /** @type {WeakMap<Event, string>} Stillwell: the quantity the cart held for that line when the stepper was tapped */
  #eventBases = new WeakMap();

  /** @type {Set<string>} Stillwell: lines whose stepper change is still in its 300ms debounce */
  #waiting = new Set();

  /**
   * Stillwell: once no stepper change is waiting or on its way, every stepper shows what the cart holds and no price
   * keeps shimmering. A number tapped while a change was on its way is kept through re-renders as "newer"; when that
   * newer change was then dropped or sent under a key that had changed, the stepper kept a number the cart did not
   * hold (4 for a cart of 3, and the next + added to the wrong number) and the line price stayed greyed.
   */
  static #settleSoon() {
    setTimeout(() => CartItemsComponent.#settle(), 0);
  }

  static #settle() {
    if (CartItemsComponent.#pendingQuantityUpdates.size) return;
    const lists = Array.from(document.querySelectorAll('cart-items-component')).filter((el) => el instanceof CartItemsComponent);
    if (lists.some((el) => /** @type {CartItemsComponent} */ (el).#waiting.size)) return;
    for (const el of /** @type {CartItemsComponent[]} */ (lists)) {
      for (const row of el.#rows()) {
        const input = row.querySelector('cart-quantity-selector-component input');
        if (input instanceof HTMLInputElement && input !== document.activeElement && input.value !== input.defaultValue) {
          input.value = input.defaultValue;
        }
      }
      resetShimmer(el);
      el.#updateCartQuantitySelectorButtonStates();
    }
  }

  /** Stillwell: last time the cart was checked against the server on returning to the tab. */
  #lastVisibleCheck = 0;

  /**
   * Stillwell: the variant part of a line key ("47785067282516:3c18…" -> "47785067282516").
   * @param {string | undefined} key
   */
  static #variantOf(key) {
    return key ? String(key).split(':')[0] : '';
  }

  /**
   * Stillwell: the stepper button or quantity field that has the focus, by its line's variant. With a discount code
   * every quantity change re-keys the line and the re-render replaces its row: the focus fell to the page each time.
   * @returns {{ variant: string, kind: string, keyboard: boolean } | null}
   */
  #focusMark() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) return null;
    const row = active.closest('[ref="cartItemRows[]"]');
    if (!(row instanceof HTMLElement)) return null;
    let kind = '';
    if (active.classList.contains('quantity-plus')) kind = 'plus';
    else if (active.classList.contains('quantity-minus')) kind = 'minus';
    else if (active.matches('input[name="updates[]"]')) kind = 'input';
    else if (active.classList.contains('cart-items__title')) kind = 'title';
    if (!kind) return null;
    return { variant: CartItemsComponent.#variantOf(row.dataset.key), kind, keyboard: active.matches(':focus-visible') };
  }

  /**
   * Stillwell: puts the focus back on the same control of the same line after a re-render, when it was lost (on the
   * quantity field, or the + button for touch, when that control is now disabled).
   * @param {{ variant: string, kind: string, keyboard: boolean } | null} mark
   */
  #restoreFocus(mark) {
    if (!mark) return;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    const row = this.#rows().find((r) => CartItemsComponent.#variantOf(r.dataset.key) === mark.variant);
    if (!row) return;
    /** @param {string} kind */
    const pick = (kind) =>
      row.querySelector(
        kind === 'plus'
          ? '.quantity-plus'
          : kind === 'minus'
            ? '.quantity-minus'
            : kind === 'title'
              ? '.cart-items__title'
              : 'input[name="updates[]"]'
      );
    let el = pick(mark.kind);
    if (el instanceof HTMLButtonElement && el.disabled) el = pick(mark.keyboard ? 'input' : 'plus');
    if (el instanceof HTMLElement && !(el instanceof HTMLButtonElement && el.disabled)) el.focus({ preventScroll: true });
  }

  /**
   * Stillwell: the line row an event came from (the stepper or the trash of that row).
   * @param {Event | undefined} event
   * @returns {HTMLElement | null}
   */
  #rowOf(event) {
    const target = event?.target;
    return target instanceof Element ? /** @type {HTMLElement | null} */ (target.closest('[ref="cartItemRows[]"]')) : null;
  }

  /**
   * Stillwell: the current position of a line, found by its key, else by its variant when only one line has it (a
   * cart with a discount code gives a line a new key on every quantity change). -1 when the line is gone.
   * @param {string | undefined} key
   * @returns {number}
   */
  #findRow(key) {
    if (!key) return -1;
    const rows = this.#rows();
    const at = rows.findIndex((row) => row.dataset.key === key);
    if (at >= 0) return at;
    const variant = CartItemsComponent.#variantOf(key);
    const same = rows.flatMap((row, i) => (CartItemsComponent.#variantOf(row.dataset.key) === variant ? [i] : []));
    return same.length === 1 ? same[0] : -1;
  }

  /** Stillwell: rows whose removal is still on its way stay out of sight after a re-render. */
  #hideRemoving() {
    if (!CartItemsComponent.#removing.size) return;
    const variants = new Set(Array.from(CartItemsComponent.#removing, (k) => CartItemsComponent.#variantOf(k)));
    for (const row of this.#rows()) {
      if (CartItemsComponent.#removing.has(row.dataset.key ?? '') || variants.has(CartItemsComponent.#variantOf(row.dataset.key))) {
        row.style.display = 'none';
      }
    }
  }

  /**
   * Stillwell: a short message under a line (and its stepper back to what the cart holds).
   * @param {HTMLElement | null | undefined} row
   * @param {string} message
   */
  #showLineError(row, message) {
    if (!row) return;
    const input = row.querySelector('cart-quantity-selector-component input');
    if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    const container = row.querySelector('[ref^="cartItemErrorContainer-"]');
    const text = row.querySelector('[ref^="cartItemError-"]');
    if (text instanceof HTMLElement) text.textContent = message;
    if (container instanceof HTMLElement) container.classList.remove('hidden');
  }

  /**
   * Stillwell: back on this tab, the cart may have changed in another one. The list is checked against the cart and
   * re-rendered when it differs, so a stale line is never changed (a + on a line removed elsewhere changed the line
   * that took its place).
   */
  #handleVisibility = () => {
    if (document.visibilityState !== 'visible' || !this.dataset.sectionId) return;
    if (Date.now() - this.#lastVisibleCheck < 2000 || CartItemsComponent.#pendingQuantityUpdates.size) return;
    this.#lastVisibleCheck = Date.now();
    this.fetchCartData()
      .then((cart) => {
        const want = (cart?.items ?? []).map((/** @type {any} */ i) => i.key + '=' + i.quantity).join('|');
        const have = this.#rows()
          .map((row) => {
            const input = row.querySelector('cart-quantity-selector-component input');
            return row.dataset.key + '=' + (input instanceof HTMLInputElement ? input.defaultValue : '');
          })
          .join('|');
        if (want === have) return;
        return this.#refreshCartSection({
          injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
        }).then(() => this.dispatchEvent(new CartSectionRestoredEvent()));
      })
      .catch(() => {});
  };

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   */
  async #refreshCartSection(options = {}) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    document.addEventListener('visibilitychange', this.#handleVisibility);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore, so the sticky summary measurement has to
        // be driven from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    // Stillwell: membership was checked when the event was dispatched (#debouncedOnChange)
    if (!(event.target instanceof Node)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Stillwell: the line the stepper belonged to when tapped, found again now (line numbers and keys go stale while
    // earlier changes re-render the list)
    const key = this.#eventKeys.get(event);
    const at = this.#findRow(key);
    const row = at >= 0 ? this.#rows()[at] : null;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event, key);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      row,
      key,
      base: this.#eventBases.get(event),
    });
    const lineItemRow = row ?? this.#rows()[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event, key) {
    // Stillwell: the row that holds the tapped trash (or stepper); line numbers go stale while changes are on their way
    const keyAt = typeof key === 'string' ? this.#findRow(key) : -1;
    const tappedRow = this.#rowOf(event);
    const cartItemRowToRemove = keyAt >= 0 ? this.#rows()[keyAt] : tappedRow?.isConnected ? tappedRow : this.#rows()[line - 1];
    const removingKey = cartItemRowToRemove?.dataset.key;
    if (cartItemRowToRemove?.classList.contains('removing') || (removingKey && CartItemsComponent.#removing.has(removingKey))) return;
    if (removingKey) CartItemsComponent.#removing.add(removingKey);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      row: cartItemRowToRemove,
    });

    if (!cartItemRowToRemove) return;

    const allRows = this.#rows();
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...allRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    // (Stillwell: rows already on their way out do not count)
    const liveRows = allRows.filter((row) => !row.classList.contains('removing') && row.style.display !== 'none');
    const isEmptyCart = rowsToRemove.length >= liveRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      const showEmpty = () => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
        if (this.isDrawer) this.#emptyDrawerFrame();
        else {
          // Stillwell: the focused trash is gone with the last line: focus goes to "Continue shopping"
          const active = document.activeElement;
          if (!active || active === document.body || !active.isConnected) {
            /** @type {HTMLElement | null} */ (this.querySelector('.cart-items__empty-button'))?.focus({ preventScroll: true });
          }
        }
      };

      // Stillwell: on phones the empty state goes in at once. The view transition snapshots the whole page and blocks
      // taps while it animates, and the /cart/change.js answer lands mid-animation: removing the last item froze the
      // screen for ~1s on phones. Desktop keeps the fade.
      if (window.matchMedia('(max-width: 749px)').matches) {
        showEmpty();
      } else {
        startViewTransition(showEmpty, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
      }

      return;
    }

    // Stillwell: the focus was on the removed line (its trash): it moves to the name of the next line (else the one
    // before), so keyboard and screen reader users keep their place (it fell to the page)
    const active = document.activeElement;
    if (active instanceof HTMLElement && rowsToRemove.some((row) => row.contains(active))) {
      const others = liveRows.filter((row) => !rowsToRemove.includes(row));
      const next =
        others.find((row) => Boolean(row.compareDocumentPosition(cartItemRowToRemove) & Node.DOCUMENT_POSITION_PRECEDING)) ??
        others[others.length - 1];
      const title = next?.querySelector('.cart-items__title');
      if (title instanceof HTMLElement) title.focus({ preventScroll: true });
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Stillwell: the rest of the drawer goes empty together with its items, the way the empty drawer is rendered
   * (snippets/cart-drawer.liquid): without the banner and free-shipping bar (snippets/sl-cart-drawer-top.liquid) and
   * with the empty-state header. They used to stay until /cart/change.js answered (~0.35s later) and then vanish.
   */
  #emptyDrawerFrame() {
    const inner = this.closest('.cart-drawer__inner');
    if (!inner) return;
    inner.classList.add('cart-drawer--empty');
    inner.querySelector('.sl-cart-top')?.remove();
    inner.querySelector('.theme-drawer__header .theme-drawer__close-button')?.classList.add('cart-drawer__close-button--empty');
    // The focused remove button is gone with the items: focus stays in the drawer, on "Continue shopping"
    const active = document.activeElement;
    if (!active || active === document.body || !active.isConnected) {
      /** @type {HTMLElement | null} */ (inner.querySelector('.cart-items__empty-button'))?.focus({ preventScroll: true });
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {HTMLElement | null} [config.row] - Stillwell: the row the change came from.
   * @param {string} [config.key] - Stillwell: that row's key when it was tapped (used when the row was re-rendered).
   * @param {string} [config.base] - Stillwell: the quantity the cart held for that line when it was tapped.
   */
  updateQuantity(config) {
    // Stillwell: queued behind any change still on its way (see #queue). The line's key is read now, while the line
    // number still points at the row the shopper touched.
    const row = config.row && config.row.isConnected ? config.row : null;
    const key = row?.dataset.key ?? config.key ?? this.#rows()[config.line - 1]?.dataset.key;
    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => {
      completeQuantityUpdate = () => resolve();
    });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);

    const run = CartItemsComponent.#queue
      .then(() => this.#runQuantityUpdate(config, key))
      .finally(() => {
        CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
        completeQuantityUpdate();
        CartItemsComponent.#settleSoon();
      });
    CartItemsComponent.#queue = run.catch(() => {});
    return run;
  }

  /**
   * Stillwell: the line rows, read from the DOM (refs can lag a mutation behind right after a morph).
   * @returns {HTMLElement[]}
   */
  #rows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[ref="cartItemRows[]"]')));
  }

  /**
   * Stillwell: lines whose stepper shows a number newer than the answer being morphed in (tapped while it was on its
   * way). The morph would show the older quantity for a moment, then the newer one.
   * @param {string | undefined} key - The line this answer is for.
   * @param {number} quantity - The quantity this answer is for.
   * @returns {Array<[string, string]>} Line key and the value on screen.
   */
  #newerQuantities(key, quantity) {
    /** @type {Array<[string, string]>} */
    const kept = [];
    for (const row of this.#rows()) {
      const input = row.querySelector('cart-quantity-selector-component input');
      if (!(input instanceof HTMLInputElement) || !row.dataset.key) continue;
      const expected = row.dataset.key === key ? String(quantity) : input.defaultValue;
      if (input.value !== expected) kept.push([row.dataset.key, input.value]);
    }
    return kept;
  }

  /** @param {Array<[string, string]>} kept */
  #keepNewerQuantities(kept) {
    if (!kept.length) return;
    const rows = this.#rows();
    for (const [key, value] of kept) {
      const at = this.#findRow(key);
      const row = at >= 0 ? rows[at] : undefined;
      const input = row?.querySelector('cart-quantity-selector-component input');
      if (input instanceof HTMLInputElement && input.value !== value) input.value = value;
      /** @type {TextComponent | null | undefined} */ (row?.querySelector('.cart-items__price text-component'))?.shimmer?.();
    }
  }

  /**
   * @param {Object} config - See updateQuantity.
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   * @param {string | undefined} key - The line's key when the change was made.
   */
  async #runQuantityUpdate(config, key) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    try {
      this.#disableCartItems();

      // Stillwell: an earlier change may have moved rows, re-keyed this line (a discount code does that on every
      // quantity change) or removed it while this one waited. The line is found again now and sent by its current
      // key, never by position (a position can point at another product). A change for a line that is gone is
      // dropped; a removal still goes out (its row is already off the screen).
      const at = this.#findRow(key);
      if (at < 0 && key && config.quantity > 0) return;
      const line = at >= 0 ? at + 1 : config.line;
      const currentKey = at >= 0 ? this.#rows()[at].dataset.key : key;
      const target = currentKey ? { id: currentKey } : { line };
      const { quantity } = config;
      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }

      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      const body = JSON.stringify({
        ...target,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      const lineId = key ?? this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: lineId, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        if (key) CartItemsComponent.#removing.delete(key);
        // Stillwell: the line is not in the cart any more (changed in another tab or window): show the cart as it is
        // now instead of Shopify's technical message
        if (/no valid id|line parameter/i.test(String(parsedResponseText.errors))) {
          const stale = new Error('The cart changed');
          stale.name = 'AbortError';
          deferredUpdatePromise.reject(stale);
          await this.#refreshCartSection();
          this.dispatchEvent(new CartSectionRestoredEvent());
          // Stillwell: the line was only given a new key (a discount code applied or removed while this change was
          // on its way: Shopify re-keys the lines): the same change goes out once more under the line's new key, if
          // that line still holds the quantity the shopper saw when tapping (a removal always). Else the cart changed
          // elsewhere and it shows as it is now. The change used to be dropped silently (+ after Apply: cart stayed x1).
          const retry = this.#retryTarget(currentKey ?? key, config);
          if (retry) {
            if (config.quantity === 0) {
              CartItemsComponent.#removing.add(retry);
              this.#hideRemoving();
            }
            return this.#runQuantityUpdate({ ...config, retried: true }, retry);
          }
          return;
        }
        this.#handleCartError(line, parsedResponseText);
        deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
        return;
      }

      const sections = /** @type {Record<string, string> | null | undefined} */ (parsedResponseText.sections);
      const sectionHTML = sections?.[this.sectionId];
      const parsedItemCount = Number(parsedResponseText.item_count);
      let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      if (!sectionHTML) {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: sections ?? undefined,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await this.#refreshCartSection();
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: sections ?? undefined,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      sectionRenderer.abortRender(this.sectionId);
      const kept = this.#newerQuantities(currentKey, quantity);
      if (quantity === 0 && key) CartItemsComponent.#removing.delete(key);
      const focusMark = this.#focusMark();
      await morphSection(this.sectionId, sectionHTML, {
        mode: this.isDrawer ? 'hydration' : 'full',
      });
      this.#keepNewerQuantities(kept);
      this.#hideRemoving();

      this.#updateCartQuantitySelectorButtonStates();
      this.#restoreFocus(focusMark);

      // Stillwell: the drawer went empty with the focus on a control that is gone: focus stays in the drawer
      if (this.isDrawer && !this.#rows().length) {
        const active = document.activeElement;
        if (!active || active === document.body || !active.isConnected) {
          /** @type {HTMLElement | null} */ (this.closest('.cart-drawer__inner')?.querySelector('.cart-items__empty-button') ?? null)?.focus({ preventScroll: true });
        }
      }
    } catch (error) {
      console.warn('[cart-items] Cart update failed:', error);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      // Stillwell: no answer (connection lost): the prices stop shimmering, the stepper goes back to what the cart
      // holds and the line says why (the stepper used to keep showing 3 while the cart held 1, and the next tap
      // saved 4). A removal that did not go out brings its line back once the cart can be read again.
      resetShimmer(this);
      if (key) CartItemsComponent.#removing.delete(key);
      const failedAt = this.#findRow(key);
      const failedRow = failedAt >= 0 ? this.#rows()[failedAt] : null;
      if (failedRow) failedRow.style.display = '';
      this.#showLineError(
        failedRow,
        navigator.onLine === false
          ? "You're offline. Check your connection and try again."
          : "We couldn't update your cart. Please try again."
      );
      if (config.quantity === 0 && !failedRow) {
        const restore = () => this.#refreshCartSection().then(() => this.dispatchEvent(new CartSectionRestoredEvent()));
        if (navigator.onLine === false) window.addEventListener('online', restore, { once: true });
        else restore();
      }
      this.#updateCartQuantitySelectorButtonStates();
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Stillwell: the line to send a change to again after a "no valid id" answer: the only line of the same variant,
   * when it still holds the quantity the shopper saw (any quantity for a removal). Once per change.
   * @param {string | undefined} key - The key the change was sent with.
   * @param {{ quantity: number, base?: string, retried?: boolean }} config
   * @returns {string | null}
   */
  #retryTarget(key, config) {
    if (config.retried || !key) return null;
    const variant = CartItemsComponent.#variantOf(key);
    const rows = this.#rows().filter((row) => CartItemsComponent.#variantOf(row.dataset.key) === variant);
    if (rows.length !== 1 || !rows[0].dataset.key || rows[0].dataset.key === key) return null;
    if (config.quantity > 0) {
      const input = rows[0].querySelector('cart-quantity-selector-component input');
      if (!(input instanceof HTMLInputElement) || config.base === undefined || input.defaultValue !== String(config.base)) return null;
    }
    return rows[0].dataset.key;
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Stillwell: read from the row in the DOM (refs can lag a re-render behind, and showed the error on another line)
    const row = this.#rows()[line - 1];
    if (row) row.style.display = '';
    this.#showLineError(row, String(parsedResponseText.errors));

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    // Stillwell: a drawer that is closed when the shopper adds opens only once the new markup is in place
    // (cart-drawer.js), so it must arrive whole: no view transition and no row animation. Read before the
    // drawer opens. The cart page and an already open drawer keep Horizon's animations.
    const animate = !this.isDrawer || Boolean(this.closest('theme-drawer')?.hasAttribute('open'));

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];

        // Stillwell: already in place (renderSectionHtml, from the add response). Morphing again would rebuild the
        // payment buttons and installments for nothing.
        const renderedEarly = CartItemsComponent.#renderedEarly.get(this.sectionId);
        CartItemsComponent.#renderedEarly.delete(this.sectionId);
        if (cartItemsHtml && cartItemsHtml === renderedEarly) {
          this.#updateCartQuantitySelectorButtonStates();
          return;
        }

        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer && animate) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && animate && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection(morphOptions);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
